/* ============================================================
   Dathent — Auth screens (Login / Signup / Forgot / Reset / Confirm)
   White-first surface from the design pack (Auth.html). Same MFA
   logic and AuthContext API as the legacy screen — only the surface
   changes.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { Icons as I } from '../components/pack-icons';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../services/api/client.js';
import ThemeToggle from '../components/AppShell/ThemeToggle';
import LangSwitch from '../components/AppShell/LangSwitch';
import Turnstile from '../components/Turnstile.jsx';
import { googleAuthEnabled } from '../lib/flags';
import { captureSitePrefillFromQuery } from '../lib/onboardingSitePrefill.js';
import { safeNext } from '../lib/safeNext.js';
import { safeReturnPath, rememberReturnPath, takeReturnPath } from '../lib/returnPath.js';
import { rememberChatGptSignupSource } from '../lib/signupSource.js';
import { googleAuthHref } from '../lib/acquisitionAttribution.js';
import { getUxAttribution } from '../utils/uxTracker.js';

/* ---------------- Shell ---------------- */
function AuthShell({ children }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="auth-page">
      <header className="auth-topbar">
        <button
          className="auth-topbar-l"
          type="button"
          onClick={() => navigate('/login')}
          style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}
          aria-label={t('auth.dathentHome')}
        >
          <img
            src="/assets/dathent-logo.png"
            alt="Dathent"
            width={26}
            height={26}
            style={{ display: 'block', borderRadius: 6, objectFit: 'contain' }}
          />
          <span>Dathent</span>
        </button>
        <div className="auth-topbar-r">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>
      <div className="auth-card">{children}</div>
    </div>
  );
}

/* ---------------- Continue with Google (shared) ---------------- */
// Error codes the auth-worker's google-oauth handler can send back on /login.
// Whitelisted so an attacker-controlled ?error= can't probe arbitrary i18n keys.
const KNOWN_GOOGLE_ERRORS = [
  'google_oauth_failed', 'google_email_unverified', 'google_missing_identity',
  'account_inactive', 'missing_code_or_state', 'invalid_or_expired_state',
  'registration_closed', 'google_auth_disabled', 'access_denied'
];

// safeNext (the ?next= allowlist: this environment's API origin, /oauth-mcp/
// paths only) lives in lib/safeNext.js — shared with PendingOAuthResume.

function authPath(path, next) {
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

// Explicit MCP next URLs return through the authorization server, which owns
// consent, pending-request ownership and the billing gate. Without a next URL,
// the app can still discover an existing pending request after authentication.

function GoogleG() {
  return (
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9086c1.7018-1.5668 2.6836-3.874 2.6836-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9086-2.2581c-.8059.54-1.8368.859-3.0478.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9573C.3477 6.1731 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.964 10.71z"/>
      <path fill="#EA4335" d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"/>
    </svg>
  );
}

// Full-page navigation (NOT a Google JS SDK) to the auth-worker's OIDC start
// endpoint via the cross-origin API base URL. Gated by googleAuthEnabled() so
// it ships dark until the backend flag + Google Cloud secrets are live.
function GoogleAuthBlock({ labelKey, returnFrom, next }) {
  const { t } = useTranslation();
  if (!googleAuthEnabled()) return null;
  // TASK (2026-09-10, ChatGPT-skip-onboarding, B5): forward the same
  // validated ?next= this page received (see Auth()'s safeNext() below) so
  // auth-worker's /api/oauth/google/start (A2) can carry the MCP plugin
  // hand-off across the Google OIDC round trip, same as the password
  // login/signup forms already do via authPath().
  const href = googleAuthHref(api.baseURL, next, getUxAttribution());
  // The click leaves the SPA (full-page nav to auth-worker's OIDC start, then
  // back to /login?... on return) — router state doesn't survive that trip.
  // Back up the requested /app path so the post-login effect above still has
  // it once we're back, same as RequireAuth's own backup (lib/returnPath.js).
  const handleClick = () => {
    const backup = safeReturnPath(returnFrom?.pathname, returnFrom?.search);
    if (backup) rememberReturnPath(backup);
  };
  return (
    <>
      <a
        className="auth-btn secondary block auth-google"
        href={href}
        onClick={handleClick}
        style={{ textDecoration: 'none' }}
      >
        <GoogleG />
        <span>{t(labelKey, 'Continue with Google')}</span>
      </a>
      <div className="auth-divider"><span>{t('auth.orDivider', 'or')}</span></div>
    </>
  );
}

/* ---------------- Login ---------------- */
function LoginCard({ next }) {
  const { t } = useTranslation();
  const { login, isAuthenticated, loading, error: ctxError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaSetupChallenge, setMfaSetupChallenge] = useState(null);
  const returnedToMcp = useRef(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      if (next) {
        // A switched account cannot resume the previous owner's pending cookie.
        // This explicit, allowlisted GET lets MCP create a fresh owned attempt.
        if (!returnedToMcp.current) {
          returnedToMcp.current = true;
          window.location.assign(next);
        }
        return;
      }
      const from = location.state?.from;
      // Default to /app (new AppShell) when there's no specific origin.
      // Sending users to "/" used to land them on the legacy MainApp.
      // Router state (`from`) is lost on a full-page round trip (a reload on
      // /login, or the Google OIDC hop back through /login?...) — fall back
      // to the sessionStorage backup RequireAuth left behind (lib/returnPath.js).
      const target = safeReturnPath(from?.pathname, from?.search) || takeReturnPath() || '/app';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, loading, location.state, navigate, next]);

  // Google OAuth redirect-back: the auth-worker sends the browser to
  // /login?<params> for every outcome. Reuse the existing MFA challenge UI for
  // mfa=required, and surface a localized message for any error code.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const attempt = params.get('attempt');
    const hasMfa = params.get('mfa') === 'required' && attempt;
    const err = params.get('error');
    if (hasMfa) {
      setMfaChallenge({ attemptId: attempt });
    } else if (err) {
      // Whitelist known codes so an attacker-controlled ?error= can't probe
      // arbitrary i18n keys; anything unknown falls back to a generic message.
      const code = KNOWN_GOOGLE_ERRORS.includes(err) ? err : 'generic';
      setFormError(t(`auth.googleErrors.${code}`));
    }
    // Strip the OAuth redirect params from the URL so a half-authenticated MFA
    // attemptId (or an error code) isn't left in browser history / access logs.
    if (hasMfa || err) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.search]);

  // Landing-hero site prefill: the marketing site's hero input sends visitors
  // to /login?site=<url>. Stash a validated value (if present) for the
  // onboarding chat to read later — additive only, independent of the OAuth
  // param handling above, and never touches the URL itself.
  useEffect(() => {
    captureSitePrefillFromQuery(window.location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(event) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requiresMfa) {
        setMfaChallenge(result);
        return;
      }
      if (result?.requiresMfaSetup) {
        setMfaSetupChallenge(result);
        return;
      }
      // The authenticated effect owns navigation for password and MFA success.
    } catch (err) {
      setFormError(err.message || t('auth.authenticationFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  if (mfaSetupChallenge) {
    return <MfaSetupCard challenge={mfaSetupChallenge} onBack={() => setMfaSetupChallenge(null)} />;
  }
  if (mfaChallenge) {
    return <MfaChallengeCard challenge={mfaChallenge} onBack={() => setMfaChallenge(null)} />;
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">{t('auth.welcomeBack')}</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>{t('auth.signInToWorkspace')}</p>
      </div>

      {next && <div className="auth-info">{t('auth.connectReturnNote')}</div>}

      <GoogleAuthBlock labelKey="auth.continueWithGoogle" returnFrom={location.state?.from} next={next} />

      <div className="auth-fields">
        <div className="field-group">
          <label className="field-label" htmlFor="login-email">{t('auth.workEmail')}</label>
          <input
            id="login-email"
            className="auth-field"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            autoComplete="email"
            required
          />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor="login-password">
            <span>{t('auth.password')}</span>
            <button type="button" className="linklike" onClick={() => navigate('/forgot')}>
              {t('auth.forgot')}
            </button>
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="login-password"
              className="auth-field"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.atLeast8Characters')}
              autoComplete="current-password"
              minLength={8}
              required
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              style={{
                position: 'absolute', right: 8, top: 8,
                background: 'none', border: 0, color: 'var(--text-subtle)',
                padding: 6, cursor: 'pointer', display: 'inline-flex'
              }}
            >
              <I.eye size={16} />
            </button>
          </div>
        </div>
        {(formError || ctxError) && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{formError || ctxError}</span>
          </div>
        )}
        <button className="auth-btn violet block" type="submit" disabled={submitting || loading}>
          {submitting ? t('auth.signingIn') : t('auth.signIn')}
        </button>
      </div>

      <p className="auth-foot">
        {t('auth.newToDathent')}{' '}
        <button type="button" className="linklike" onClick={() => navigate(authPath('/signup', next))}>
          {t('auth.createAWorkspace')}
        </button>
      </p>
    </form>
  );
}

/* ---------------- Signup (open registration + Turnstile) ----------------
   Real self-serve registration. Gated server-side by PUBLIC_SIGNUP_ENABLED:
   while it's off the API returns 403 and we degrade to an early-access prompt
   (the public pre-register waitlist), so the form is never a dead end. */
const PRE_REGISTER_URL = 'https://dathent.ai/pre-register';

function SignupCard({ next }) {
  const { t } = useTranslation();
  const { reloadUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState(null);
  const [tsReset, setTsReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [inviteOnly, setInviteOnly] = useState(false);

  // Landing-hero site prefill: the marketing site's hero input can also send
  // visitors straight to /signup?site=<url> — same capture as LoginCard.
  useEffect(() => {
    captureSitePrefillFromQuery(window.location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    if (!token) { setError(t('auth.completeVerification', 'Please complete the verification below.')); return; }
    setSubmitting(true);
    try {
      await api.auth.signup(email.trim(), password, name.trim() || null, token, next);
      await reloadUser({ fresh: true });
      if (next) window.location.assign(next);
      else navigate('/app', { replace: true });
    } catch (err) {
      if (err?.status === 403) { setInviteOnly(true); }
      else { setError(err?.message || t('auth.couldNotCreateAccount', 'Could not create your account.')); }
      // Turnstile tokens are single-use — clear the spent one AND reset the
      // widget so it mints a fresh token (else the button stays disabled).
      setToken(null);
      setTsReset((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  }

  if (inviteOnly) {
    return (
      <>
        <div>
          <h2 className="auth-h">{t('auth.openingSoonTitle', "We're opening access soon")}</h2>
          <p className="auth-sub" style={{ marginTop: 8 }}>
            {t('auth.openingSoonBody', 'Self-serve signup is not open yet. Join the early-access list and we will let you in.')}
          </p>
        </div>
        <a className="auth-btn violet block" href={PRE_REGISTER_URL} style={{ textAlign: 'center', textDecoration: 'none' }}>
          {t('auth.joinEarlyAccess', 'Join early access')}
        </a>
        <p className="auth-foot">
          <button type="button" className="linklike" onClick={() => navigate(authPath('/login', next))}>{t('auth.backToSignIn')}</button>
        </p>
      </>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">{t('auth.createYourAccount', 'Create your account')}</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>{t('auth.createAccountSub', 'Set up your workspace — it only takes a minute.')}</p>
      </div>
      {next && <div className="auth-info">{t('auth.connectReturnNote')}</div>}
      <GoogleAuthBlock labelKey="auth.continueWithGoogle" next={next} />
      <div className="auth-fields">
        <div className="field-group">
          <label className="field-label" htmlFor="su-name">{t('auth.yourName', 'Your name')}</label>
          <input id="su-name" className="auth-field" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('auth.namePlaceholder', 'Your name')} autoComplete="name" />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor="su-email">{t('auth.workEmail')}</label>
          <input id="su-email" className="auth-field" type="email" value={email} onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }} placeholder={t('auth.emailPlaceholder')} autoComplete="email" required />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor="su-password">{t('auth.password')}</label>
          <div style={{ position: 'relative' }}>
            <input id="su-password" className="auth-field" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.atLeast8Characters')} autoComplete="new-password" minLength={8} required style={{ paddingRight: 40 }} />
            <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')} style={{ position: 'absolute', right: 8, top: 8, background: 'none', border: 0, color: 'var(--text-subtle)', padding: 6, cursor: 'pointer', display: 'inline-flex' }}><I.eye size={16} /></button>
          </div>
        </div>
        <Turnstile onVerify={setToken} resetSignal={tsReset} />
        {error && (<div className="auth-error"><I.alert size={14} /><span>{error}</span></div>)}
        <button className="auth-btn violet block" type="submit" disabled={submitting || !token}>
          {submitting ? t('auth.creatingAccount', 'Creating account…') : t('auth.createAccount', 'Create account')}
        </button>
      </div>
      <p className="auth-foot">
        {t('auth.alreadyHaveAccount', 'Already have an account?')}{' '}
        <button type="button" className="linklike" onClick={() => navigate(authPath('/login', next))}>{t('auth.signIn')}</button>
      </p>
    </form>
  );
}

/* ---------------- Forgot password ---------------- */
function ForgotCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.auth.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || t('auth.unableToSendResetLink'));
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <>
        <div className="auth-mail-badge">
          <I.send size={22} />
        </div>
        <div>
          <h2 className="auth-h" style={{ textAlign: 'center' }}>{t('auth.checkYourEmail')}</h2>
          <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
            {t('auth.weSentResetLinkTo')} <b style={{ color: 'var(--text)' }}>{email}</b>. {t('auth.itExpiresIn10Minutes')}
          </p>
        </div>
        <div className="auth-info" style={{ textAlign: 'left' }}>
          {t('auth.cantFindItCheckSpam')}
        </div>
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <button className="auth-btn secondary" style={{ flex: 1 }} type="button" onClick={() => { setSent(false); setEmail(''); }}>
            {t('auth.useADifferentEmail')}
          </button>
          <button className="auth-btn violet" style={{ flex: 1 }} type="button" onClick={submit} disabled={submitting}>
            {submitting ? t('auth.sending') : t('auth.resendLink')}
          </button>
        </div>
      </>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">{t('auth.resetYourPassword')}</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          {t('auth.enterWorkEmailForResetLink')}
        </p>
      </div>
      <div className="auth-fields">
        <div className="field-group">
          <label className="field-label" htmlFor="forgot-email">{t('auth.workEmail')}</label>
          <input
            id="forgot-email"
            className="auth-field"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            autoComplete="email"
            required
          />
        </div>
        {error && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{error}</span>
          </div>
        )}
        <button className="auth-btn violet block" type="submit" disabled={submitting}>
          {submitting ? t('auth.sending') : t('auth.sendResetLink')}
        </button>
      </div>
      <p className="auth-foot">
        <button type="button" className="linklike" onClick={() => navigate('/login')}>
          {t('auth.backToSignIn')}
        </button>
      </p>
    </form>
  );
}

/* ---------------- Reset password ---------------- */
function ResetCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    if (!token) { setError(t('auth.resetLinkMissingTokenRequestNew')); return; }
    if (password !== confirm) { setError(t('auth.passwordsDoNotMatch')); return; }
    setSubmitting(true);
    try {
      await api.auth.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || t('auth.unableToResetPassword'));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <>
        <div className="auth-mail-badge"><I.check size={22} /></div>
        <div>
          <h2 className="auth-h" style={{ textAlign: 'center' }}>{t('auth.passwordUpdated')}</h2>
          <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
            {t('auth.useNewPasswordToSignIn')}
          </p>
        </div>
        <button className="auth-btn violet block" type="button" onClick={() => navigate('/login')}>
          {t('auth.backToSignInPlain')}
        </button>
      </>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">{t('auth.createNewPassword')}</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          {t('auth.chooseNewPasswordForAccount')}
        </p>
      </div>
      {!token && (
        <div className="auth-info">
          {t('auth.resetLinkMissingTokenRequestEmail')}
        </div>
      )}
      <div className="auth-fields">
        <div className="field-group">
          <label className="field-label" htmlFor="reset-password">{t('auth.newPassword')}</label>
          <input
            id="reset-password"
            className="auth-field"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.atLeast8Characters')}
            minLength={8}
            autoComplete="new-password"
            required
          />
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor="reset-confirm">{t('auth.confirmPassword')}</label>
          <input
            id="reset-confirm"
            className="auth-field"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t('auth.reEnterPassword')}
            minLength={8}
            autoComplete="new-password"
            required
          />
        </div>
        {error && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{error}</span>
          </div>
        )}
        <button className="auth-btn violet block" type="submit" disabled={submitting || !token}>
          {submitting ? t('auth.updating') : t('auth.updatePassword')}
        </button>
      </div>
      <p className="auth-foot">
        <button type="button" className="linklike" onClick={() => navigate('/login')}>
          {t('auth.backToSignIn')}
        </button>
      </p>
    </form>
  );
}

/* ---------------- Confirm email (consume the email link) ----------------
   The verification email points here with ?token=…. We POST it to /api/auth/
   verify (unauthenticated — the token is the proof), then, if the click happened
   in the same browser the user signed up in, refresh /me so the email-wall
   clears and send them into the app. Clicked elsewhere → prompt to sign in. */
function ConfirmCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, reloadUser } = useAuth();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  // 'verifying' | 'success' | 'error' | 'notoken'
  const [state, setState] = useState(token ? 'verifying' : 'notoken');
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // guard React 18 StrictMode double-invoke (token is single-use)
    (async () => {
      try {
        await api.auth.verifyEmail(token);
        if (isAuthenticated) { await reloadUser().catch(() => {}); }
        setState('success');
      } catch (_) {
        setState('error');
      }
    })();
  }, [token, isAuthenticated, reloadUser]);

  if (state === 'verifying') {
    return (
      <>
        <div className="auth-mail-badge"><I.send size={22} /></div>
        <div>
          <h2 className="auth-h" style={{ textAlign: 'center' }}>{t('auth.confirmingYourEmail', 'Confirming your email…')}</h2>
          <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
            {t('auth.oneMoment', 'One moment while we confirm your address.')}
          </p>
        </div>
      </>
    );
  }

  if (state === 'success') {
    return (
      <>
        <div className="auth-mail-badge"><I.check size={22} /></div>
        <div>
          <h2 className="auth-h" style={{ textAlign: 'center' }}>{t('auth.emailConfirmed', 'Email confirmed')}</h2>
          <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
            {isAuthenticated
              ? t('auth.emailConfirmedContinue', "You're all set — let's finish setting up your workspace.")
              : t('auth.emailConfirmedSignIn', 'Your email is confirmed. Sign in to continue.')}
          </p>
        </div>
        <button className="auth-btn violet block" type="button" onClick={() => navigate(isAuthenticated ? '/app' : '/login', { replace: true })}>
          {isAuthenticated ? t('auth.continue', 'Continue') : t('auth.backToSignInPlain')}
        </button>
      </>
    );
  }

  // error / notoken
  return (
    <>
      <div className="auth-mail-badge"><I.alert size={22} /></div>
      <div>
        <h2 className="auth-h" style={{ textAlign: 'center' }}>{t('auth.confirmLinkInvalid', 'This confirmation link is invalid or expired')}</h2>
        <p className="auth-sub" style={{ marginTop: 8, textAlign: 'center' }}>
          {t('auth.confirmLinkInvalidBody', 'Sign in and request a fresh confirmation link from the prompt shown there.')}
        </p>
      </div>
      <button className="auth-btn violet block" type="button" onClick={() => navigate(isAuthenticated ? '/app' : '/login', { replace: true })}>
        {isAuthenticated ? t('auth.continue', 'Continue') : t('auth.backToSignInPlain')}
      </button>
    </>
  );
}

/* ---------------- MFA challenge (post-password) ---------------- */
function MfaChallengeCard({ challenge, onBack }) {
  const { t } = useTranslation();
  const { verifyMfaLogin, verifyMfaBackupCode, sendMfaEmailCode, verifyMfaEmailCode } = useAuth();
  const [mode, setMode] = useState('totp');
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'backup') {
        await verifyMfaBackupCode(challenge.attemptId, backupCode.trim());
      } else if (mode === 'email') {
        await verifyMfaEmailCode(challenge.attemptId, emailCode.trim());
      } else {
        await verifyMfaLogin(challenge.attemptId, code.trim());
      }
      // LoginCard observes the authenticated identity and preserves its next.
    } catch (err) {
      setError(err.message || t('auth.couldNotVerifyCode'));
    } finally {
      setSubmitting(false);
    }
  }

  async function requestEmailCode() {
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await sendMfaEmailCode(challenge.attemptId);
      setEmailSent(true);
      setMode('email');
      setNotice(t('auth.weSentACodeTo', { email: challenge.emailHint || t('auth.yourEmail') }));
    } catch (err) {
      setError(err.message || t('auth.couldNotSendEmailCode'));
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting ||
    (mode === 'totp' && code.length !== 6) ||
    (mode === 'email' && emailCode.length !== 6) ||
    (mode === 'backup' && !backupCode.trim());

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', background: 'var(--accent-soft)', color: 'var(--accent-text)',
        borderRadius: 999, alignSelf: 'flex-start', fontSize: 11.5, fontWeight: 500,
      }}>
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        {mode === 'backup' ? t('auth.recoveryCode') : mode === 'email' ? t('auth.emailCode') : t('auth.authenticatorApp')}
      </div>
      <div>
        <h2 className="auth-h">
          {mode === 'backup' ? t('auth.enterARecoveryCode') : t('auth.enterYour6DigitCode')}
        </h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          {mode === 'backup'
            ? t('auth.eachSavedCodeWorksOnce')
            : mode === 'email'
              ? t('auth.codeSentTo', { email: challenge.emailHint || t('auth.yourEmail') })
              : t('auth.openAuthenticatorEnterCode')}
        </p>
      </div>

      {mode === 'totp' && (
        <CodeInput6 value={code} onChange={setCode} autoFocus />
      )}
      {mode === 'email' && (
        <CodeInput6 value={emailCode} onChange={setEmailCode} autoFocus />
      )}
      {mode === 'backup' && (
        <div className="field-group">
          <label className="field-label" htmlFor="mfa-backup">{t('auth.recoveryCode')}</label>
          <input
            id="mfa-backup"
            className="auth-field mono"
            type="text"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            placeholder="xxxx-xxxx-xxxx"
            autoFocus
          />
        </div>
      )}

      {notice && <div className="auth-info">{notice}</div>}
      {error && (
        <div className="auth-error">
          <I.alert size={14} />
          <span>{error}</span>
        </div>
      )}

      <button className="auth-btn violet block" type="submit" disabled={submitDisabled}>
        {submitting ? t('auth.verifying') : t('auth.verify')}
      </button>

      <div className="resend-line" style={{ flexWrap: 'wrap', gap: 8 }}>
        {mode !== 'totp' && (
          <button type="button" className="linklike" onClick={() => { setMode('totp'); setError(null); setNotice(null); }}>
            {t('auth.useAuthenticator')}
          </button>
        )}
        {mode !== 'backup' && (
          <button type="button" className="linklike" onClick={() => { setMode('backup'); setError(null); setNotice(null); }}>
            {t('auth.useRecoveryCode')}
          </button>
        )}
        {challenge.emailCodeAvailable && (
          <button type="button" className="linklike" onClick={requestEmailCode} disabled={submitting}>
            {emailSent ? t('auth.resendEmailCode') : t('auth.emailMeACode')}
          </button>
        )}
        <button type="button" className="linklike" style={{ color: 'var(--text-subtle)' }} onClick={onBack}>
          {t('auth.backToSignInPlain')}
        </button>
      </div>
    </form>
  );
}

/* ---------------- MFA setup (forced enrollment) ---------------- */
function MfaSetupCard({ challenge, onBack }) {
  const { t } = useTranslation();
  const { verifyRequiredMfaSetup, reloadUser } = useAuth();
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [codesSaved, setCodesSaved] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!challenge?.otpauthUrl) return;
    QRCode.toDataURL(challenge.otpauthUrl, { width: 168, margin: 1 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [challenge]);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    if (backupCodes) {
      if (!codesSaved) { setError(t('auth.saveBackupCodesBeforeContinuing')); return; }
      await reloadUser({ fresh: true });
      return;
    }
    if (!/^\d{6}$/.test(code)) { setError(t('auth.enterValid6DigitCode')); return; }
    setSubmitting(true);
    try {
      const result = await verifyRequiredMfaSetup(challenge.attemptId, code);
      setBackupCodes(result.backupCodes || []);
    } catch (err) {
      setError(err.message || t('auth.couldNotEnable2FA'));
    } finally {
      setSubmitting(false);
    }
  }

  if (backupCodes) {
    return (
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <div>
          <h2 className="auth-h">{t('auth.saveTheseRecoveryCodes')}</h2>
          <p className="auth-sub" style={{ marginTop: 6 }}>
            {t('auth.eachWorksOnceStoreSafe')}
          </p>
        </div>
        <div className="auth-success">
          <b>{t('auth.twoFactorIsEnabled')}</b> {t('auth.yourNextSignInWillAskForCode')}
        </div>
        <div className="recovery-codes">
          {backupCodes.map((c) => <code key={c}>{c}</code>)}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="auth-btn secondary" style={{ flex: 1 }}
            onClick={() => navigator.clipboard?.writeText(backupCodes.join('\n'))}>
            <I.copy size={14} /> {t('auth.copy')}
          </button>
          <button type="button" className="auth-btn secondary" style={{ flex: 1 }}
            onClick={() => {
              const blob = new Blob([backupCodes.join('\n') + '\n'], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'dathent-backup-codes.txt';
              link.click();
              URL.revokeObjectURL(url);
            }}>
            <I.download size={14} /> {t('auth.download')}
          </button>
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={codesSaved} onChange={(e) => setCodesSaved(e.target.checked)}
                 style={{ accentColor: 'var(--accent)', width: 16, height: 16, marginTop: 2 }} />
          <span>{t('auth.iHaveSavedTheseCodes')}</span>
        </label>
        {error && (
          <div className="auth-error">
            <I.alert size={14} />
            <span>{error}</span>
          </div>
        )}
        <button type="submit" className="auth-btn violet block" disabled={!codesSaved}>
          {t('auth.enterDathent')}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: 'contents' }}>
      <div>
        <h2 className="auth-h">{t('auth.setUpTwoFactor')}</h2>
        <p className="auth-sub" style={{ marginTop: 6 }}>
          {t('auth.scanQrThenEnterFirstCode')}
        </p>
      </div>
      <div className="auth-qr">
        {qrDataUrl
          ? <img src={qrDataUrl} alt={t('auth.authenticatorQrCode')} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-subtle)', fontSize: 12 }}>{t('auth.loading')}</div>}
      </div>
      {challenge.manualKey && (
        <details style={{ fontSize: 12.5, color: 'var(--text-subtle)' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--accent-text)' }}>{t('auth.cantScanShowSetupKey')}</summary>
          <div style={{ marginTop: 8, padding: 8, background: 'var(--bg-subtle)', borderRadius: 'var(--r-md)', wordBreak: 'break-all', fontFamily: 'var(--font-mono)' }}>
            {challenge.manualKey}
          </div>
        </details>
      )}
      <div className="field-group">
        <label className="field-label">{t('auth.first6DigitCode')}</label>
        <CodeInput6 value={code} onChange={setCode} autoFocus />
      </div>
      {error && (
        <div className="auth-error">
          <I.alert size={14} />
          <span>{error}</span>
        </div>
      )}
      <button type="submit" className="auth-btn violet block" disabled={submitting || code.length !== 6}>
        {submitting ? t('auth.verifying') : t('auth.confirmAndEnable')}
      </button>
      <p className="auth-foot">
        <button type="button" className="linklike" onClick={onBack}>{t('auth.backToSignIn')}</button>
      </p>
    </form>
  );
}

/* ---------------- 6-digit code input ---------------- */
function CodeInput6({ value, onChange, autoFocus }) {
  const refs = useRef([]);
  const chars = (value || '').padEnd(6, ' ').slice(0, 6).split('');

  const setChar = (i, ch) => {
    const next = (value || '').padEnd(6, ' ').split('');
    next[i] = ch;
    onChange(next.join('').replace(/ /g, ''));
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !e.currentTarget.value && i > 0) {
      refs.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) refs.current[i + 1]?.focus();
  };

  const onInput = (i, e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
    setChar(i, v);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const raw = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (raw) {
      e.preventDefault();
      onChange(raw);
      refs.current[Math.min(5, raw.length - 1)]?.focus();
    }
  };

  return (
    <div className="code-input">
      {chars.map((ch, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          maxLength={1}
          inputMode="numeric"
          pattern="[0-9]*"
          value={ch === ' ' ? '' : ch}
          className={ch !== ' ' ? 'has-value' : ''}
          onChange={(e) => onInput(i, e)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          autoFocus={autoFocus && i === 0}
        />
      ))}
    </div>
  );
}

/* ---------------- Public entry ---------------- */
export const Auth = ({ mode = 'login' }) => {
  const location = useLocation();
  const next = useMemo(() => safeNext(new URLSearchParams(location.search).get('next')), [location.search]);

  useEffect(() => {
    if (next) rememberChatGptSignupSource();
  }, [next]);

  return (
    <AuthShell>
      {mode === 'login' && <LoginCard next={next} />}
      {mode === 'signup' && <SignupCard next={next} />}
      {mode === 'forgot' && <ForgotCard />}
      {mode === 'reset' && <ResetCard />}
      {mode === 'confirm' && <ConfirmCard />}
    </AuthShell>
  );
};

export default Auth;
